import {
  GoAButton,
  GoAButtonGroup,
  GoAFormItem,
  GoAModal,
  GoARadioGroup,
  GoARadioItem,
  GoASpacer,
} from '@abgov/react-components';
import { BaseWizardProps } from '../wizard/wizard-step-base';
import { useEffect, useState } from 'react';
import { AppDispatch, RootState } from '../../state/store';
import { useDispatch, useSelector } from 'react-redux';
import { updateCollision } from '../../state/slices/collisionSlice';
import { Collision } from '../../models/collision';
import { removeAllValidationErrorNotifications } from '../../state/slices/notificationSlice';
import {
  defaultValidate,
  WizardButtonGroup,
} from '../wizard/wizard-button-group-component';
import { IsInReviewEditMode } from '../wizard/wizard-component';
import { setCollisionSubTypeUpdated } from '../../state/slices/configurationSlice';

const useAppDispatch = () => useDispatch<AppDispatch>();
const useAppSelector: <TSelected>(
  selector: (state: RootState) => TSelected,
) => TSelected = useSelector;

export interface SideOfRoadComponentProps extends BaseWizardProps {
  side: 'Left' | 'Right';
}
export const SideOfRoadComponent = ({
  side,
  navigationConfiguration,
}: SideOfRoadComponentProps) => {
  const dispatch = useAppDispatch();

  const errorText = 'Choose one collision detail';
  const [formError, setFormError] = useState('');
  const [selection, setSelection] = useState('0');

  const [nextPage, setNextPage] = useState('');
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [modalHeading, setModalHeading] = useState('');

  const stateConfiguration = useAppSelector(
    (state: RootState) => state.configuration,
  );

  const stateCollision = useAppSelector((state: RootState) => state.collision);

  useEffect(() => {
    if (
      stateCollision.collision &&
      stateCollision.collision.objectIdentification
    ) {
      setSelection(
        stateCollision.collision.objectIdentification.toString() ?? '0',
      );
    }
  }, [stateCollision]);

  useEffect(() => {
    const pageConfig = navigationConfiguration.pagingConfiguration.find(
      (item) => item.default !== undefined,
    );

    setNextPage(pageConfig?.default ?? 'error');
  }, []);

  useEffect(() => {
    if (
      stateConfiguration.uiTriggers.collisionTypeUpdated ||
      stateConfiguration.uiTriggers.collisionSubTypeUpdated
    ) {
      setModalHeading('Your collision type changes have been saved');
    } else {
      setModalHeading('');
    }
  }, [
    stateConfiguration.uiTriggers.collisionTypeUpdated,
    stateConfiguration.uiTriggers.collisionSubTypeUpdated,
  ]);

  const save = () => {
    const primaryEventType =
      stateConfiguration.configuration!.primaryEvent.find((x) => {
        return x.value.toLowerCase() == `Off Road ${side}`.toLowerCase();
      });

    let objectIdentificationType =
      stateConfiguration.configuration!.objectIdentification.find((x) => {
        return x.id == parseInt(selection);
      });

    let updatedCollision: Collision = { ...stateCollision.collision };

    if (primaryEventType) {
      if (
        navigationConfiguration.updateMetadata &&
        navigationConfiguration.stepId
      ) {
        const stepUpdated = navigationConfiguration.updateMetadata(
          updatedCollision,
          navigationConfiguration.stepId!,
          navigationConfiguration.stageCompleted
            ? navigationConfiguration.stageCompleted
            : false,
          !navigationConfiguration.stageCompleted,
        );

        if (stepUpdated && updatedCollision.metadata) {
          updatedCollision.metadata = {
            ...updatedCollision.metadata,
            formState: stepUpdated,
          };
        }
      }

      const prevPrimaryEvent = updatedCollision.primaryEvent;
      const prevObjectIdentification = updatedCollision.objectIdentification;
      updatedCollision.primaryEvent = primaryEventType.id ?? 0;
      updatedCollision.objectIdentification = objectIdentificationType?.id ?? 0;
      updatedCollision.animalType = null;
      updatedCollision.witnessOrCameraAvailable = null;

      dispatch(updateCollision(updatedCollision));

      if (navigationConfiguration.save) {
        navigationConfiguration.save();
        if (
          (IsInReviewEditMode() &&
            prevObjectIdentification !=
              updatedCollision.objectIdentification) ||
          prevPrimaryEvent != updatedCollision.primaryEvent
        ) {
          dispatch(setCollisionSubTypeUpdated(true));
        }
      }
    }
  };

  const navigate = (page: string) => {
    if (IsInReviewEditMode()) {
      setShowReviewModal(true);
    } else {
      if (navigationConfiguration.navigate) {
        navigationConfiguration.navigate(page);
      }
    }
  };
  return (
    <div>
      <GoAModal
        calloutVariant="important"
        actions={
          <GoAButtonGroup alignment="end">
            <GoAButton
              onClick={() => {
                setShowReviewModal(false);
              }}
              type="tertiary"
            >
              Cancel
            </GoAButton>
            <GoAButton
              onClick={() => {
                setShowReviewModal(false);
                navigationConfiguration.navigate('#review');
              }}
              type="primary"
            >
              View report summary
            </GoAButton>
          </GoAButtonGroup>
        }
        heading={<div>{modalHeading}</div>}
        open={showReviewModal}
      >
        <div>
          Click 'View report summary to return to the report review page.
        </div>
      </GoAModal>
      <h1>Collision type</h1>
      The collision details section is the starting point for drivers to
      identify the type of collision they were involved in. In some cases you’ll
      be prompted to add some additional details depending on your type of
      collision.
      <h2>My vehicle ran off {side.toLocaleLowerCase()} side of the road</h2>
      Select one additional collision detail below that occurred
      <GoASpacer vSpacing="l" />
      <GoAFormItem error={formError}>
        <GoARadioGroup
          name="collisionTypeGroup"
          error={formError.length > 0}
          value={selection}
          onChange={(item, id) => {
            setSelection(id);
            setFormError('');
            dispatch(removeAllValidationErrorNotifications());
          }}
        >
          <GoARadioItem
            value="146"
            label={`Vehicle ran off ${side.toLowerCase()} side of road`}
          ></GoARadioItem>
          <GoARadioItem
            value="136"
            label="Vehicle ran off road and hit an object "
            description={
              <span style={{ font: 'var(--goa-typography-body-xs)' }}>
                eg. tree, sign, fence, guard rail, cable barriers
              </span>
            }
          ></GoARadioItem>
        </GoARadioGroup>
      </GoAFormItem>
      <WizardButtonGroup
        nextPage={nextPage}
        validate={(): boolean => {
          return defaultValidate(
            selection,
            '0',
            errorText,
            setFormError,
            navigationConfiguration.errorNotification,
          );
        }}
        showViewSummaryButton={!IsInReviewEditMode()}
        goToReview={navigationConfiguration.goToReview}
        save={save}
        navigate={navigate}
      />
    </div>
  );
};
